import {
  ActionIcon,
  Anchor,
  Button,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Tooltip
} from '@mantine/core'
import { FormErrors } from '@mantine/form'
import { IconCirclePlus, IconTrash } from '@tabler/icons-react'
import { ReactNode, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ApplicationType } from '@/types/app'
import {
  BusinessLogicRuleOperator,
  LabelGroupType,
  LibraryLogicType,
  NormalizedBusinessLogicRule,
  NormalizedLabelGroup,
  ParameterType,
  SubLabelOperator
} from '@/types/businessLogic'
import { LabelingType } from '@/types/dataset'
import { useDeploymentStepperContext } from '../../DeploymentStepperContext'
import { AlertConfig } from '../AlertConfig/AlertConfig'
import {
  ExtractionConfig,
  ExtractionConfigValues
} from '../ExtractionConfig/ExtractionConfig'
import { RepeatedAlertConfig } from '../RepeatedAlertConfig/RepeatedAlertConfig'
import { ObjectMultiSelect } from './ObjectMultiSelect'
import { ObjectSelect } from './ObjectSelect'

const resetExtractionValues = {
  is_ocr_enabled: false,
  ocr_label_ids: [],
  is_qr_barcode_enabled: false,
  qr_barcode_label_ids: []
}

type ConfigWrapperProps = {
  children: ReactNode
}

const ConfigWrapper = ({ children }: ConfigWrapperProps) => {
  return (
    <Paper bg="white" pt={1} px="xs">
      {children}
    </Paper>
  )
}

type RuleBuilderProps = {
  logicType: LibraryLogicType
  logicParameterType: ParameterType
  ruleOperator: NormalizedBusinessLogicRule['operator']
  labelGroups: NormalizedLabelGroup[]
  errors: FormErrors
  onLabelGroupAdd: () => void
  onLabelGroupRemove: (index: number) => void
  onLabelGroupChange: (index: number, labelGroup: NormalizedLabelGroup) => void
  onOperatorChange: (value: NormalizedBusinessLogicRule['operator']) => void
}
export const RuleBuilder = ({
  logicType,
  logicParameterType,
  ruleOperator,
  labelGroups,
  errors,
  onLabelGroupAdd,
  onLabelGroupRemove,
  onLabelGroupChange,
  onOperatorChange
}: RuleBuilderProps) => {
  const intl = useIntl()

  const {
    appType,
    primaryModel,
    primaryLabels,
    secondaryModel,
    secondaryLabels,
    uniqueLabels
  } = useDeploymentStepperContext()

  const primaryLabelsData = useMemo(() => {
    return [
      {
        group: primaryModel?.name || '',
        items: primaryLabels.map((label) => ({
          value: label.id,
          label: label.name,
          color: label.color
        }))
      }
    ]
  }, [primaryModel, primaryLabels])

  const secondaryLabelsData = useMemo(() => {
    return [
      // Classification classes cannot be selected as sub-labels in a label group, they are shown in a separate dropdown
      ...(appType === ApplicationType.MultiStage &&
      secondaryModel?.labeling_type !== LabelingType.Classification
        ? [
            {
              group: secondaryModel?.name || '',
              items: secondaryLabels.map((label) => ({
                value: label.id,
                label: label.name,
                color: label.color
              }))
            }
          ]
        : []),
      {
        group: primaryModel?.name || '',
        items: primaryLabels.map((label) => ({
          value: label.id,
          label: label.name,
          color: label.color
        }))
      }
    ]
  }, [primaryModel, primaryLabels, secondaryModel, secondaryLabels, appType])

  const classificationLabelsData = useMemo(() => {
    return [
      {
        group: secondaryModel?.name || '',
        items: [
          {
            value: '',
            label: intl.formatMessage({
              id: 'logic.rules.object.type.any'
            }),
            color: 'gray'
          },
          ...secondaryLabels.map((label) => ({
            value: label.id,
            label: label.name,
            color: label.color
          }))
        ]
      }
    ]
  }, [secondaryModel, secondaryLabels, intl])

  const isClassificationEnabled =
    appType === ApplicationType.MultiStage &&
    secondaryModel?.labeling_type === LabelingType.Classification

  const errorMessage = Object.values(errors)[0]

  const handleLabelChange = (index: number, label_id: string) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      ...resetExtractionValues,
      label_id
    })
  }

  const handleMakeGroup = (index: number) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      type: LabelGroupType.Group
    })
  }

  const handleSubLabelChange = (index: number, label_ids: string[]) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      ...resetExtractionValues,
      sub_label_ids: label_ids
    })
  }

  const handleSubLabelOperatorChange = (
    index: number,
    operator: NormalizedLabelGroup['sub_label_operator']
  ) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      sub_label_operator: operator
    })
  }

  const handleClassificationLabelChange = (
    index: number,
    classification_label_id: string | null
  ) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      classification_label_id: classification_label_id || null
    })
  }

  const handleThresholdChange = (index: number, threshold: number | '') => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      threshold: threshold === '' ? 0 : threshold
    })
  }

  const handleAlertConfigChange = (
    index: number,
    alertType: 'alert_on_enter' | 'alert_on_exit',
    dwellTime: number | null
  ) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      ...(alertType === 'alert_on_exit' ? resetExtractionValues : {}), // Disable OCR and QR/barcode when alert on exit is enabled
      alert_on_exit: alertType === 'alert_on_exit',
      min_dwell_time: dwellTime
    })
  }

  const handleExtractionConfigChange = (
    index: number,
    type: 'ocr' | 'qr_barcode',
    config: ExtractionConfigValues
  ) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      ...(type === 'ocr' && {
        is_ocr_enabled: config.isExtractionEnabled,
        ocr_label_ids: config.extractionLabelIds
      }),
      ...(type === 'qr_barcode' && {
        is_qr_barcode_enabled: config.isExtractionEnabled,
        qr_barcode_label_ids: config.extractionLabelIds
      })
    })
  }

  const handleRepeatedAlertConfigChange = (
    index: number,
    isRepeatedAlertEnabled: boolean
  ) => {
    onLabelGroupChange(index, {
      ...labelGroups[index],
      alert_on_unique: !isRepeatedAlertEnabled
    })
  }

  return (
    <>
      {logicType === LibraryLogicType.ObjectDetection && (
        <Text size="md" fw={600} mb="xs">
          <FormattedMessage id="logic.objectDetection.labelName" />
        </Text>
      )}

      {logicType === LibraryLogicType.ObjectCount && (
        <Text size="md" fw={600} mb="xs">
          <FormattedMessage id="logic.objectCount.labelName" />
        </Text>
      )}

      {labelGroups.map((labelGroup, index) => (
        <Stack key={labelGroup.uuid} gap="lg">
          <Paper
            pos="relative"
            pt={labelGroup.type === LabelGroupType.Single ? 'xl' : 'sm'}
            pb="sm"
            px="sm"
            bg="gray.2"
          >
            {labelGroup.type === LabelGroupType.Single && (
              <Anchor
                pos="absolute"
                top={10}
                left={12}
                component="button"
                type="button"
                size="xs"
                fw={600}
                c="blue.7"
                onClick={() => handleMakeGroup(index)}
              >
                <FormattedMessage id="logic.rules.object.makeGroup" />
              </Anchor>
            )}

            <Grid gutter={4}>
              <Grid.Col span="auto">
                <Stack gap={4}>
                  <Group wrap="nowrap" gap={4}>
                    <ObjectSelect
                      w="100%"
                      label={
                        <FormattedMessage id="logic.rules.object.select.label.detection" />
                      }
                      placeholder={
                        labelGroup.type === LabelGroupType.Group
                          ? intl.formatMessage({
                              id: 'logic.rules.object.select.placeholder.parent'
                            })
                          : intl.formatMessage({
                              id: 'logic.rules.object.select.placeholder'
                            })
                      }
                      value={labelGroup.label_id}
                      data={primaryLabelsData}
                      onChange={(value) =>
                        handleLabelChange(index, value as string)
                      }
                    />

                    {labelGroup.label_id && (
                      <Tooltip
                        label={
                          <FormattedMessage id="logic.rules.object.type.disabledTooltip" />
                        }
                        disabled={isClassificationEnabled}
                        w={320}
                        multiline
                      >
                        <ObjectSelect
                          w="100%"
                          label={
                            <FormattedMessage id="logic.rules.object.select.label.classification" />
                          }
                          placeholder={intl.formatMessage({
                            id: 'logic.rules.object.type.any'
                          })}
                          disabled={!isClassificationEnabled}
                          value={labelGroup.classification_label_id || ''}
                          data={classificationLabelsData}
                          onChange={(value) =>
                            handleClassificationLabelChange(index, value)
                          }
                        />
                      </Tooltip>
                    )}
                  </Group>

                  {labelGroup.type === LabelGroupType.Group && (
                    <>
                      <Select
                        maw={100}
                        size="xs"
                        value={labelGroup.sub_label_operator}
                        allowDeselect={false}
                        data={[
                          {
                            value: SubLabelOperator.With,
                            label: intl.formatMessage({
                              id: 'logic.rules.group.operator.with'
                            })
                          },
                          {
                            value: SubLabelOperator.Without,
                            label: intl.formatMessage({
                              id: 'logic.rules.group.operator.without'
                            })
                          }
                        ]}
                        comboboxProps={{
                          width: 160,
                          position: 'bottom-start'
                        }}
                        checkIconPosition="right"
                        onChange={(value) =>
                          handleSubLabelOperatorChange(
                            index,
                            value as NormalizedLabelGroup['sub_label_operator']
                          )
                        }
                      />

                      <ObjectMultiSelect
                        placeholder={intl.formatMessage({
                          id: 'logic.rules.object.select.placeholder.child'
                        })}
                        value={labelGroup.sub_label_ids}
                        data={secondaryLabelsData}
                        onChange={(value) => handleSubLabelChange(index, value)}
                      />
                    </>
                  )}
                </Stack>
              </Grid.Col>

              <Grid.Col span="content">
                <Group gap={4} wrap="nowrap" align="start" mih={56}>
                  {logicType === LibraryLogicType.ObjectCount && (
                    <NumberInput
                      size="xs"
                      label={
                        <FormattedMessage id="logic.rules.object.count.label" />
                      }
                      maw={80}
                      min={0}
                      allowDecimal={false}
                      value={labelGroup.threshold}
                      onChange={(value) =>
                        handleThresholdChange(index, value as number | '')
                      }
                    />
                  )}

                  <ActionIcon
                    variant="subtle"
                    size="xs"
                    radius="xl"
                    mt={30}
                    onClick={() => onLabelGroupRemove(index)}
                  >
                    <IconTrash style={{ width: '80%', height: '80%' }} />
                  </ActionIcon>
                </Group>
              </Grid.Col>
            </Grid>

            {labelGroup.label_id &&
              logicParameterType === ParameterType.ROI && (
                <Stack mt="lg" gap="xs">
                  <Group gap="xs">
                    <ConfigWrapper>
                      <AlertConfig
                        alertType={
                          labelGroup.alert_on_exit
                            ? 'alert_on_exit'
                            : 'alert_on_enter'
                        }
                        dwellTime={labelGroup.min_dwell_time}
                        isAlertOnExitDisabled={
                          logicType !== LibraryLogicType.ObjectDetection
                        }
                        alertOnExitDisabledTooltip={
                          <FormattedMessage id="logic.alert.onExit.disabledTooltip" />
                        }
                        onChange={(alertType, dwellTime) =>
                          handleAlertConfigChange(index, alertType, dwellTime)
                        }
                      />
                    </ConfigWrapper>

                    <ConfigWrapper>
                      <RepeatedAlertConfig
                        isEnabled={!labelGroup.alert_on_unique}
                        onChange={(isEnabled) =>
                          handleRepeatedAlertConfigChange(index, isEnabled)
                        }
                      />
                    </ConfigWrapper>
                  </Group>

                  <Group gap="xs">
                    <ConfigWrapper>
                      <ExtractionConfig
                        type="ocr"
                        disabled={labelGroup.alert_on_exit}
                        extractionConfig={{
                          isExtractionEnabled: labelGroup.is_ocr_enabled,
                          extractionLabelIds: labelGroup.ocr_label_ids
                        }}
                        labels={uniqueLabels.filter(
                          (label) =>
                            labelGroup.label_id === label.id ||
                            labelGroup.sub_label_ids.includes(label.id)
                        )}
                        onChange={(extractionConfig) =>
                          handleExtractionConfigChange(
                            index,
                            'ocr',
                            extractionConfig
                          )
                        }
                      />
                    </ConfigWrapper>

                    <ConfigWrapper>
                      <ExtractionConfig
                        type="qr_barcode"
                        disabled={labelGroup.alert_on_exit}
                        extractionConfig={{
                          isExtractionEnabled: labelGroup.is_qr_barcode_enabled,
                          extractionLabelIds: labelGroup.qr_barcode_label_ids
                        }}
                        labels={uniqueLabels.filter(
                          (label) =>
                            labelGroup.label_id === label.id ||
                            labelGroup.sub_label_ids.includes(label.id)
                        )}
                        onChange={(extractionConfig) =>
                          handleExtractionConfigChange(
                            index,
                            'qr_barcode',
                            extractionConfig
                          )
                        }
                      />
                    </ConfigWrapper>
                  </Group>
                </Stack>
              )}
          </Paper>

          {index < labelGroups.length - 1 && (
            <Select
              maw={100}
              size="sm"
              checkIconPosition="right"
              radius="xl"
              mb="lg"
              value={ruleOperator}
              allowDeselect={false}
              data={[
                {
                  value: BusinessLogicRuleOperator.OR,
                  label: intl.formatMessage({ id: 'logic.rules.operator.or' })
                },
                {
                  value: BusinessLogicRuleOperator.AND,
                  label: intl.formatMessage({ id: 'logic.rules.operator.and' })
                }
              ]}
              onChange={(value) =>
                onOperatorChange(
                  value as NormalizedBusinessLogicRule['operator']
                )
              }
            />
          )}
        </Stack>
      ))}

      {errorMessage && (
        <Text size="xs" mb="sm" c="red">
          {errorMessage}
        </Text>
      )}

      <Button
        size="xs"
        mt="md"
        leftSection={<IconCirclePlus size={16} />}
        onClick={onLabelGroupAdd}
      >
        <FormattedMessage id="logic.rules.object.add" />
      </Button>
    </>
  )
}
